import { useEffect, useRef } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useInboxUnreadCount } from '../../lib/useInboxCounts';
import { usePermissions } from '../../lib/usePermissions';
import { EmployeeSearch } from './EmployeeSearch';

type Props = {
  onOpenMobileNav: () => void;
};

export function TopBar({ onOpenMobileNav }: Props) {
  const navigate = useNavigate();
  const { moduleEnabled } = usePermissions();
  const { data: unreadData } = useInboxUnreadCount();

  // The badge and tooltip both show the Inbox folder total; Sent and Completed are excluded.
  const count = unreadData?.count ?? 0;
  const inboxLabel = count === 0 ? 'Inbox' : `Inbox - ${count} item${count === 1 ? '' : 's'}`;

  const searchRef = useRef<HTMLInputElement>(null);
  const hamburgerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (
        e.key === '/' &&
        !['INPUT', 'TEXTAREA'].includes((e.target as HTMLElement).tagName)
      ) {
        e.preventDefault();
        searchRef.current?.focus();
      }
      if (
        (e.metaKey || e.ctrlKey) && e.key === 'k' &&
        !['INPUT', 'TEXTAREA'].includes((e.target as HTMLElement).tagName)
      ) {
        e.preventDefault();
        searchRef.current?.focus();
      }
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  return (
    <header
      aria-label="Top bar"
      className="sticky top-0 z-20 flex h-14 shrink-0 items-center justify-between border-b border-slate-200 bg-surface px-3 md:px-5"
    >
      {/* Left: hamburger (mobile only). The wordmark/logo now lives in the sidebar. */}
      <div className="flex items-center gap-2">
        {/* Hamburger button — hidden at md+ */}
        {/* Not a <Button>: the mobile nav returns focus here on close and the shared Button
            exposes no ref (a @payforge/ui change). Sized off the control
            scale - this one is md:hidden, so only the 44px touch height ever renders. */}
        {/* eslint-disable-next-line local/no-self-sized-control -- needs a button ref; see above */}
        <button
          ref={hamburgerRef}
          type="button"
          aria-label="Open navigation"
          aria-expanded={false}
          onClick={onOpenMobileNav}
          className="flex h-11 w-11 items-center justify-center rounded-md text-slate-600 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 md:hidden"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path d="M3 6h18M3 12h18M3 18h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
      </div>

      {/* Right: the global search field, then a cluster of icon buttons. The search is a bordered
          pill (its edge is its visual boundary) while the icons are glyphs centred in larger
          hit-circles with transparent padding on both sides. Under a single uniform gap the
          icon-to-icon spacing (two paddings) reads wider than search-to-icon (one padding), so the
          icons get their own tighter gap on desktop. On mobile every control is an equal-size circle,
          so the cluster gap matches the container gap and the rhythm stays even. */}
      <div className="flex items-center gap-1 md:gap-2">
        {/* Global employee search */}
        <EmployeeSearch inputRef={searchRef} />

        {/* Icon-button cluster (inbox + settings) */}
        <div className="flex items-center gap-1 md:gap-0.5">
          {/* Inbox tray */}
          {/* Not a <Button>: this is one half of a matched pair with the settings NavLink below,
              which renders an <a> and so can never be one. Both already carry the control scale
              (h-8 desktop, 44px touch); making only this half a Button would split the cluster's
              shared slate treatment in two. */}
          {/* eslint-disable-next-line local/no-self-sized-control -- pairs with the settings NavLink; see above */}
          <button
            type="button"
            aria-label={inboxLabel}
            title={inboxLabel}
            onClick={() => void navigate('/inbox')}
            className="relative flex h-11 w-11 md:h-8 md:w-8 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            {count > 0 && (
              <span
                aria-hidden="true"
                // 10px bold ink, so it needs 4.5:1. `red-500` + white was 3.76:1 in light and
                // 2.84:1 in dark (the ramp mirrors, so the fill LIGHTENS while the ink stays
                // white). `red-600` + the theme's inverse ink is 4.71:1 light / 7.56:1 dark.
                className="absolute top-1 right-1 md:top-0 md:right-0 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-red-600 px-0.5 text-[10px] font-bold leading-none text-fg-inverse"
              >
                {count > 9 ? '9+' : count}
              </span>
            )}
          </button>

          {/* Settings gear — shown when the effective access level grants Settings access,
              mirroring the backend gate on the settings endpoints. */}
          {moduleEnabled('settings_access') ? (
            <NavLink
              to="/settings"
              aria-label="Settings"
              className="flex h-11 w-11 md:h-8 md:w-8 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path
                  d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </NavLink>
          ) : null}
        </div>
      </div>
    </header>
  );
}
